import React, { useState } from 'react';
import { Button } from './button';
import { CheckCircle, FileCheck, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { 
  UpdateDocumentApprovalStatus, 
  UpdateRegistryApprovalStatus, 
  UpdateTeamMemberApprovalStatus 
} from '../../redux/Project/specificationSaga';

type ApiType = 'pipe' | 'registry' | 'teamMember';

interface ApproveButtonProps {
  docId: number;
  isApproved: boolean;
  apiType?: ApiType;
  reportType?: string;  // Required for pipe documents (material/IRN/QC inspection)
  onStatusChange?: (docId: number, newStatus: 'approved' | 'pending') => void;
  disabled?: boolean;
  className?: string;
}

/**
 * Reusable approve button component for document approval.
 * Handles API call, loading state, and status toggle internally.
 * 
 * @param apiType - Specifies which API endpoint to use:
 *   - 'pipe' (default): PUT /pipeDocuments/updateApprovalStatus
 *   - 'registry': PUT /data360/registry/updateApprovalStatus
 *   - 'teamMember': PUT /data360/teamMembers/updateApprovalStatus
 */
export const ApproveButton: React.FC<ApproveButtonProps> = ({
  docId,
  isApproved,
  apiType = 'pipe',
  reportType,
  onStatusChange,
  disabled = false,
  className
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [currentStatus, setCurrentStatus] = useState<'approved' | 'pending'>(
    isApproved ? 'approved' : 'pending'
  );

  // Sync with prop changes
  React.useEffect(() => {
    setCurrentStatus(isApproved ? 'approved' : 'pending');
  }, [isApproved]);

  // Get the appropriate API function based on apiType
  const getApiFunction = () => {
    switch (apiType) {
      case 'registry':
        return UpdateRegistryApprovalStatus;
      case 'teamMember':
        return UpdateTeamMemberApprovalStatus;
      case 'pipe':
      default:
        return UpdateDocumentApprovalStatus;
    }
  };

  const handleClick = async () => {
    if (!docId || isLoading) return;

    setIsLoading(true);
    const newStatus = currentStatus === 'approved' ? 'pending' : 'approved';

    try {
      const apiFunction = getApiFunction();
      const response = await apiFunction([{
        docId,
        approvalStatus: newStatus,
        ...(reportType && { reportType })
      }]);

      if (response) {
        setCurrentStatus(newStatus);
        onStatusChange?.(docId, newStatus);
      }
    } catch (error) {
      console.error('Failed to update approval status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const isCurrentlyApproved = currentStatus === 'approved';

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleClick}
      disabled={disabled || isLoading || !docId}
      title={isCurrentlyApproved ? 'Click to remove approval' : 'Click to approve'}
      className={cn(
        "flex items-center gap-1.5 h-8 px-3 text-[12px] font-semibold",
        isCurrentlyApproved
          ? 'bg-[rgba(31,154,58,0.08)] hover:bg-[rgba(31,154,58,0.14)] text-galvanizing-green border-[rgba(31,154,58,0.25)]'
          : 'bg-white hover:bg-page-bg text-text-grey border-[#E2E5EA]',
        className
      )}
    >
      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : isCurrentlyApproved ? (
        <>
          <CheckCircle className="h-4 w-4" />
          Approved
        </>
      ) : (
        <>
          <FileCheck className="h-4 w-4" />
          Approve
        </>
      )}
    </Button>
  );
};
